"use client";

import {
  type Ref,
  useState,
  type MouseEvent,
  type FormEventHandler,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
// import {
//     SignInButton,
//     SignUpButton,
//     SignedIn,
//     SignedOut,
//     UserButton,
// } from '@clerk/nextjs'
import { UserButton } from "@daveyplate/better-auth-ui";
import { TbAward, TbHeart } from "react-icons/tb";
import { GrUserAdmin } from "react-icons/gr";
import { SiLichess } from "react-icons/si";
import { useUserInfo } from "./user-info-provider";
import { authClient } from "~/utils/auth-client";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { formatRating } from "./utils";
import { UserRoleEnum } from "~/lib/objects";
import {
  PgnPlayerNamesAccountMenu,
  pgnPlayerNamesActionHref,
} from "~/features/userPgnPlayerNames/AccountMenuAction";
import { UserPgnPlayerNamesEditor } from "~/features/userPgnPlayerNames/UserPgnPlayerNamesEditor";
import { useAdminAccess } from "~/features/adminAccess/useAdminAccess";

export interface AuthHeaderProps {
  userRating?: Ref<number>;
}

export function AuthHeader({}: AuthHeaderProps) {
  const { userInfo } = useUserInfo();
  const sessionState = authClient.useSession();
  const { data: session, error } = sessionState;
  const isPending = sessionState.isPending ||
    ("isRefetching" in sessionState && sessionState.isRefetching === true);
  const access = useAdminAccess();
  const { canRequest } = access;
  const canRequestRef = useRef(canRequest);
  canRequestRef.current = canRequest;
  const retryAccess = access.retry;
  const pathname = usePathname();
  const [openPgnPlayerNames, setOpenPgnPlayerNames] = useState(false);
  const openPgnEditor = useCallback(() => setOpenPgnPlayerNames(true), []);
  const accountControls = useRef<HTMLDivElement>(null);
  const header = useRef<HTMLElement>(null);
  const canChangeLichessPassword =
    userInfo?.role === UserRoleEnum.ADMIN && pathname?.startsWith("/admin");

  // State for reset rating dialog
  const [openReset, setOpenReset] = useState(false);
  const [login, setLogin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgVariant, setMsgVariant] = useState<"error" | "info" | "success">(
    "error",
  );
  // State for set life rating dialog
  const [openSetLife, setOpenSetLife] = useState(false);
  const [loginLife, setLoginLife] = useState("");
  const [lifeRatingVal, setLifeRatingVal] = useState<string | number>("");
  const [submittingLife, setSubmittingLife] = useState(false);
  const [msgLife, setMsgLife] = useState<string | null>(null);
  const [msgLifeVariant, setMsgLifeVariant] = useState<
    "error" | "info" | "success"
  >("error");
  const [openLichessPassword, setOpenLichessPassword] = useState(false);
  const [lichessPassword, setLichessPassword] = useState("");
  const [lichessPasswordConfirm, setLichessPasswordConfirm] = useState("");
  const [lichessPasswordSubmitting, setLichessPasswordSubmitting] =
    useState(false);
  const [lichessPasswordMsg, setLichessPasswordMsg] = useState<string | null>(
    null,
  );
  const requestEpoch = useRef({ reset: 0, life: 0, password: 0 });
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const observedLifetime = useRef(access.lifetime);
  useEffect(() => {
    if (observedLifetime.current === access.lifetime) return;
    observedLifetime.current = access.lifetime;
    requestEpoch.current.reset++;
    requestEpoch.current.life++;
    requestEpoch.current.password++;
    setOpenReset(false);
    setLogin("");
    setMsg(null);
    setSubmitting(false);
    setOpenSetLife(false);
    setLoginLife("");
    setLifeRatingVal("");
    setMsgLife(null);
    setSubmittingLife(false);
    setOpenLichessPassword(false);
    setLichessPassword("");
    setLichessPasswordConfirm("");
    setLichessPasswordMsg(null);
    setLichessPasswordSubmitting(false);
  }, [access.lifetime]);
  const isAnyAdminDialogOpen =
    openReset || openSetLife || openLichessPassword || openPgnPlayerNames;
  const adminDialogOpen = useRef(isAnyAdminDialogOpen);
  adminDialogOpen.current = isAnyAdminDialogOpen;
  const restorePgnFocus = useCallback(() => {
    if (adminDialogOpen.current) return;
    if (!canRequestRef.current) {
      header.current?.focus();
      return;
    }
    const trigger = accountControls.current?.querySelector<HTMLElement>(
      'button[aria-haspopup="menu"]',
    );
    const fallback = header.current?.querySelector<HTMLElement>(
      'button:not([disabled]):not([data-admin-access-retry]), a[href]',
    );
    (trigger ?? fallback ?? header.current)?.focus();
  }, []);

  const handleOpenReset = (e?: MouseEvent) => {
    e?.preventDefault();
    if (!canRequest) return;
    requestEpoch.current.reset++;
    setMsg(null);
    setOpenReset(true);
  };

  const handleOpenSetLife = (e?: MouseEvent) => {
    e?.preventDefault();
    if (!canRequest) return;
    requestEpoch.current.life++;
    setMsgLife(null);
    setOpenSetLife(true);
  };

  const handleOpenLichessPassword = (e?: MouseEvent) => {
    e?.preventDefault();
    if (!canRequest) return;
    requestEpoch.current.password++;
    setLichessPasswordMsg(null);
    setLichessPassword("");
    setLichessPasswordConfirm("");
    setOpenLichessPassword(true);
  };

  const closeLichessPasswordDialog = () => {
    requestEpoch.current.password++;
    setOpenLichessPassword(false);
    setLichessPassword("");
    setLichessPasswordConfirm("");
    setLichessPasswordMsg(null);
    setLichessPasswordSubmitting(false);
  };

  // Use a non-async handler (rule: no-misused-promises); run async flow inside a voided IIFE
  const handleSubmitReset: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!canRequest || submitting) return;
    const lifetime = access.lifetime;
    const request = ++requestEpoch.current.reset;
    const current = () => mounted.current && requestEpoch.current.reset === request;

    void (async () => {
      if (!login.trim()) {
        setMsg("Логин обязателен");
        setMsgVariant("error");
        return;
      }
      try {
        setSubmitting(true);
        setMsg(null);
        const resp = await fetch("/api/users/reset-rating", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ login }),
        });

        if (!current() || !access.isCurrent(lifetime)) return;
        if (access.reportDenial(lifetime, resp.status)) return;

        // Safely parse and narrow the response
        type ResetRatingResponse = { success: boolean; message?: string };
        const isResetResp = (val: unknown): val is ResetRatingResponse => {
          return (
            typeof val === "object" &&
            val !== null &&
            typeof (val as Record<string, unknown>).success === "boolean"
          );
        };

        let dataUnknown: unknown = null;
        try {
          dataUnknown = await resp.json();
        } catch {
          // ignore JSON parse errors; treat as unsuccessful
        }

        if (!resp.ok || !isResetResp(dataUnknown) || !dataUnknown.success) {
          const fallback = "Не удалось сбросить рейтинг";
          const message: string =
            isResetResp(dataUnknown) && typeof dataUnknown.message === "string"
              ? dataUnknown.message
              : fallback;
          throw new Error(message);
        }

        access.settle(lifetime, current, () => {
          setOpenReset(false);
          setLogin("");
          setMsg(null);
        });
      } catch (err) {
        const message: string =
          err instanceof Error ? err.message : "Не удалось сбросить рейтинг";
        console.error("Error resetting rating:", message);
        access.settle(lifetime, current, () => {
          setMsg(message);
          setMsgVariant("error");
        });
      } finally {
        access.settle(lifetime, current, () => setSubmitting(false));
      }
    })();
  };

  const handleSubmitSetLife: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!canRequest || submittingLife) return;
    const lifetime = access.lifetime;
    const request = ++requestEpoch.current.life;
    const current = () => mounted.current && requestEpoch.current.life === request;
    void (async () => {
      if (!loginLife.trim()) {
        setMsgLife("Логин обязателен");
        setMsgLifeVariant("error");
        return;
      }
      if (lifeRatingVal === "" || lifeRatingVal === null) {
        setMsgLife("Укажите рейтинг");
        setMsgLifeVariant("error");
        return;
      }
      try {
        setSubmittingLife(true);
        setMsgLife(null);
        const resp = await fetch("/api/users/set-life-rating", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            login: loginLife,
            lifeRating: Number(lifeRatingVal),
          }),
        });
        if (!current() || !access.isCurrent(lifetime)) return;
        if (access.reportDenial(lifetime, resp.status)) return;
        type SetLifeResp = { success: boolean; message?: string };
        const isSetLifeResp = (val: unknown): val is SetLifeResp =>
          typeof val === "object" &&
          val !== null &&
          typeof (val as Record<string, unknown>).success === "boolean";
        let dataUnknown: unknown = null;
        try {
          dataUnknown = await resp.json();
        } catch {
          /* noop */
        }
        if (!resp.ok || !isSetLifeResp(dataUnknown) || !dataUnknown.success) {
          const fallback = "Не удалось установить рейтинг";
          const message: string =
            isSetLifeResp(dataUnknown) &&
            typeof dataUnknown.message === "string"
              ? dataUnknown.message
              : fallback;
          throw new Error(message);
        }
        access.settle(lifetime, current, () => {
          setOpenSetLife(false);
          setLoginLife("");
          setLifeRatingVal("");
          setMsgLife(null);
        });
      } catch (err) {
        const message: string =
          err instanceof Error ? err.message : "Не удалось установить рейтинг";
        console.error("Error setting life rating:", message);
        access.settle(lifetime, current, () => {
          setMsgLife(message);
          setMsgLifeVariant("error");
        });
      } finally {
        access.settle(lifetime, current, () => setSubmittingLife(false));
      }
    })();
  };

  const handleSubmitLichessPassword: FormEventHandler<HTMLFormElement> = (
    e,
  ) => {
    e.preventDefault();
    if (!canRequest || lichessPasswordSubmitting) return;
    const lifetime = access.lifetime;
    const request = ++requestEpoch.current.password;
    const current = () => mounted.current && requestEpoch.current.password === request;

    void (async () => {
      const newPassword = lichessPassword.trim();
      const confirmPassword = lichessPasswordConfirm.trim();

      if (!newPassword) {
        setLichessPasswordMsg("Пароль не может быть пустым");
        return;
      }
      if (newPassword !== confirmPassword) {
        setLichessPasswordMsg("Пароли не совпадают");
        return;
      }

      try {
        setLichessPasswordSubmitting(true);
        setLichessPasswordMsg(null);

        const resp = await fetch("/api/admin/lichess-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: newPassword }),
        });

        if (!current() || !access.isCurrent(lifetime)) return;
        if (access.reportDenial(lifetime, resp.status)) return;

        type LichessPasswordResponse = {
          success: boolean;
          message?: string;
        };
        const isLichessPasswordResponse = (
          val: unknown,
        ): val is LichessPasswordResponse =>
          typeof val === "object" &&
          val !== null &&
          typeof (val as Record<string, unknown>).success === "boolean";

        let dataUnknown: unknown = null;
        try {
          dataUnknown = await resp.json();
        } catch {
          /* noop */
        }

        if (
          !resp.ok ||
          !isLichessPasswordResponse(dataUnknown) ||
          !dataUnknown.success
        ) {
          const fallback = "Не удалось изменить пароль lichess";
          const message: string =
            isLichessPasswordResponse(dataUnknown) &&
            typeof dataUnknown.message === "string"
              ? dataUnknown.message
              : fallback;
          throw new Error(message);
        }

        access.settle(lifetime, current, closeLichessPasswordDialog);
      } catch (err) {
        const message: string =
          err instanceof Error
            ? err.message
            : "Не удалось изменить пароль lichess";
        console.error("Error setting Lichess password:", message);
        access.settle(lifetime, current, () => setLichessPasswordMsg(message));
      } finally {
        access.settle(lifetime, current, () => setLichessPasswordSubmitting(false));
      }
    })();
  };

  // Safety: always restore body pointer-events once all admin dialogs are closed
  useEffect(() => {
    if (!isAnyAdminDialogOpen) {
      try {
        document.body?.style?.removeProperty?.("pointer-events");
      } catch {
        /* noop */
      }
    }

    return () => {
      try {
        document.body?.style?.removeProperty?.("pointer-events");
      } catch {
        /* noop */
      }
    };
  }, [isAnyAdminDialogOpen]);

  const accessMessage = (access.status === "checking" || access.status === "error") && (
        <div role={access.status === "error" ? "alert" : "status"}>
          {access.status === "error" ? <>
            Не удалось проверить доступ.
            <Button data-admin-access-retry disabled={access.status !== "error"} onClick={retryAccess}>Повторить проверку</Button>
          </> : "Проверка доступа…"}
        </div>
      );

  return (
    <header
      ref={header}
      tabIndex={-1}
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      {!isPending && <>
      {!session && !error && (
        <div className="text-red-500">Пожалуйста, войдите в систему</div>
      )}
      {session && userInfo && (
        <div className="container flex h-24 items-center px-4">
          <Link href="/" className="mr-6 mt-4 flex items-center space-x-2">
            <Image
              src="/logo.png"
              alt="Logo"
              className="h-8"
              width={110}
              height={32}
            />
          </Link>

          <div
            ref={accountControls}
            className="absolute right-0 flex items-center gap-2"
          >
            <div className="flex flex-row items-center gap-1">
              {formatRating(userInfo?.rating ?? 0)}
              <TbAward />
            </div>
            <div className="flex flex-row items-center gap-1">
              {formatRating(userInfo?.lifeRating ?? 0)}
              <TbHeart />
            </div>
            <PgnPlayerNamesAccountMenu onOpen={openPgnEditor}>
            <UserButton
              className="bg-transparent text-foreground"
              localization={{ SIGN_OUT: "Выйти" }}
              disableDefaultLinks={true}
              additionalLinks={canRequest ? [
                {
                  label: "Панель админа",
                  icon: <GrUserAdmin />,
                  href: "/admin",
                },
                ...(userInfo.role === UserRoleEnum.ADMIN
                  ? [{
                      label: "Имена игрока в PGN",
                      href: pgnPlayerNamesActionHref,
                      signedIn: true,
                    }]
                  : []),
                ...(canChangeLichessPassword
                  ? [
                      {
                        label: (
                          <Label
                            data-testid="admin-open-lichess-password"
                            onClick={handleOpenLichessPassword}
                          >
                            Изменить пароль lichess
                          </Label>
                        ),
                        icon: <SiLichess />,
                        href: "#",
                        signedIn: true,
                      },
                    ]
                  : []),
                {
                  label: (
                    <Label
                      data-testid="admin-open-reset"
                      onClick={handleOpenReset}
                    >
                      Сброс рейтинга
                    </Label>
                  ),
                  icon: <TbAward />,
                  href: "#",
                  signedIn: true,
                },
                {
                  label: (
                    <Label
                      data-testid="admin-open-setlife"
                      onClick={handleOpenSetLife}
                    >
                      Установить рейтинг &quot;в жизни&quot;
                    </Label>
                  ),
                  icon: <TbHeart />,
                  href: "#",
                  signedIn: true,
                },
              ] : []}
            />
            </PgnPlayerNamesAccountMenu>
          </div>

        </div>
      )}
      </>}
      {!isAnyAdminDialogOpen && accessMessage}
          {/* Reset rating dialog */}
          <Dialog
            open={openReset}
            onOpenChange={(v) => {
              if (!v) requestEpoch.current.reset++;
              setOpenReset(v);
              if (!v) {
                setMsg(null);
                setLogin("");
                setSubmitting(false);
              }
            }}
          >
            <DialogContent>
              {accessMessage}
              <DialogHeader>
                <DialogTitle>Сброс рейтинга</DialogTitle>
                <DialogDescription>
                  Введите логин пользователя, чтобы сбросить его рейтинг до
                  значения по умолчанию.
                </DialogDescription>
              </DialogHeader>

              {msg && (
                <div
                  role="alert"
                  className={`${msgVariant === "error" ? "border-red-600 bg-black text-red-300" : msgVariant === "success" ? "border-green-600 bg-green-500 text-green-700" : "border-blue-600 bg-blue-50 text-blue-700"} rounded-md border p-3 text-sm`}
                >
                  {msg}
                </div>
              )}

              <form onSubmit={handleSubmitReset} className="space-y-4">
                <div className="space-y-2">
                  {/* <Label htmlFor="reset-login">Логин</Label> */}
                  <Input
                    id="reset-login"
                    disabled={!canRequest || submitting}
                    data-testid="reset-login-input"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    placeholder="Логин пользователя"
                    required
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { requestEpoch.current.reset++; setOpenReset(false); }}
                    disabled={submitting}
                  >
                    Отмена
                  </Button>
                  <Button
                    data-testid="reset-submit"
                    type="submit"
                    disabled={!canRequest || submitting}
                  >
                    {submitting ? "Сброс..." : "Сбросить"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          {/* Set life rating dialog */}
          <Dialog
            open={openSetLife}
            onOpenChange={(v) => {
              if (!v) requestEpoch.current.life++;
              setOpenSetLife(v);
              if (!v) {
                setMsgLife(null);
                setLoginLife("");
                setLifeRatingVal("");
                setSubmittingLife(false);
              }
            }}
          >
            <DialogContent>
              {accessMessage}
              <DialogHeader>
                <DialogTitle>Установить рейтинг &quot;в жизни&quot;</DialogTitle>
                <DialogDescription>
                  Введите логин пользователя и новое значение рейтинга (можно с
                  десятичной частью).
                </DialogDescription>
              </DialogHeader>

              {msgLife && (
                <div
                  role="alert"
                  className={`${msgLifeVariant === "error" ? "border-red-600 bg-black text-red-300" : msgLifeVariant === "success" ? "border-green-600 bg-green-500 text-green-700" : "border-blue-600 bg-blue-50 text-blue-700"} rounded-md border p-3 text-sm`}
                >
                  {msgLife}
                </div>
              )}

              <form onSubmit={handleSubmitSetLife} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    id="setlife-login"
                    disabled={!canRequest || submittingLife}
                    data-testid="setlife-login-input"
                    value={loginLife}
                    onChange={(e) => setLoginLife(e.target.value)}
                    placeholder="Логин"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Input
                    id="setlife-rating"
                    disabled={!canRequest || submittingLife}
                    data-testid="setlife-rating-input"
                    value={lifeRatingVal}
                    onChange={(e) => setLifeRatingVal(e.target.value)}
                    placeholder="Рейтинг"
                    required
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { requestEpoch.current.life++; setOpenSetLife(false); }}
                    disabled={submittingLife}
                  >
                    Отмена
                  </Button>
                  <Button
                    data-testid="setlife-submit"
                    type="submit"
                    disabled={!canRequest || submittingLife}
                  >
                    {submittingLife ? "Установка..." : "Установить"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog
            open={openLichessPassword}
            onOpenChange={(open) => {
              if (!open) {
                closeLichessPasswordDialog();
              }
            }}
          >
            <DialogContent>
              {accessMessage}
              <DialogHeader>
                <DialogTitle>Изменить пароль lichess</DialogTitle>
                <DialogDescription>
                  Задайте новый пароль для открытия нерешенных задач на
                  lichess.org.
                </DialogDescription>
              </DialogHeader>

              {lichessPasswordMsg && (
                <div
                  role="alert"
                  className="rounded-md border border-red-600 bg-black p-3 text-sm text-red-300"
                >
                  {lichessPasswordMsg}
                </div>
              )}

              <form onSubmit={handleSubmitLichessPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="lichess-password-new">Новый пароль</Label>
                  <Input
                    id="lichess-password-new"
                    disabled={!canRequest || lichessPasswordSubmitting}
                    data-testid="lichess-password-new-input"
                    value={lichessPassword}
                    onChange={(e) => {
                      setLichessPassword(e.target.value);
                      if (lichessPasswordMsg) {
                        setLichessPasswordMsg(null);
                      }
                    }}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lichess-password-confirm">
                    Подтверждение пароля
                  </Label>
                  <Input
                    id="lichess-password-confirm"
                    disabled={!canRequest || lichessPasswordSubmitting}
                    data-testid="lichess-password-confirm-input"
                    value={lichessPasswordConfirm}
                    onChange={(e) => {
                      setLichessPasswordConfirm(e.target.value);
                      if (lichessPasswordMsg) {
                        setLichessPasswordMsg(null);
                      }
                    }}
                    autoComplete="new-password"
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeLichessPasswordDialog}
                    disabled={lichessPasswordSubmitting}
                  >
                    Отмена
                  </Button>
                  <Button
                    data-testid="lichess-password-submit"
                    type="submit"
                    disabled={!canRequest || lichessPasswordSubmitting}
                  >
                    {lichessPasswordSubmitting ? "Сохранение..." : "Сохранить"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
      <UserPgnPlayerNamesEditor
        open={openPgnPlayerNames}
        onOpenChange={setOpenPgnPlayerNames}
        onRestoreFocus={restorePgnFocus}
      />
    </header>
  );
}
